import { jsPDF } from 'jspdf';
import convert from 'convert-units';
import { ReactPlannerUtils, ReactPlannerConstants } from 'architecrange';

const { GeometryUtils } = ReactPlannerUtils;
const { UNIT_CENTIMETER } = ReactPlannerConstants;

const DIM_STYLE = 'stroke:#0096fd;stroke-width:1';
const TEXT_STYLE =
  "text-anchor:middle;font-size:12px;font-family:'Courier New',Courier,monospace;font-weight:bold;fill:#0096fd";

function escapeXml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function tagsIncludeWall(tags) {
  if (!tags) return false;
  if (typeof tags.includes === 'function') return tags.includes('wall');
  if (typeof tags.contains === 'function') return tags.contains('wall');
  const arr = tags.toArray ? tags.toArray() : tags;
  return Array.isArray(arr) && arr.indexOf('wall') !== -1;
}

function isWallLine(state, lineType) {
  if (lineType === 'wall') return true;
  const tags = state.getIn(['catalog', 'elements', lineType, 'info', 'tag']);
  return tagsIncludeWall(tags);
}

function formatWallLength(lengthCm, unit) {
  const display =
    unit === UNIT_CENTIMETER ? lengthCm : convert(lengthCm).from(UNIT_CENTIMETER).to(unit);
  return `${Number(display).toFixed(2)} ${unit}`;
}

function wallDimensionMarkup(state) {
  const scene = state.get('scene');
  const unit = scene.get('unit');
  const parts = [];

  scene.get('layers').forEach(layer => {
    if (!layer.get('visible')) return;
    layer.get('lines').forEach(line => {
      if (!isWallLine(state, line.get('type'))) return;
      const v0 = layer.getIn(['vertices', line.getIn(['vertices', 0])]);
      const v1 = layer.getIn(['vertices', line.getIn(['vertices', 1])]);
      if (!v0 || !v1) return;
      let x1 = v0.get('x');
      let y1 = v0.get('y');
      let x2 = v1.get('x');
      let y2 = v1.get('y');
      if (x1 > x2) {
        const t = x1;
        x1 = x2;
        x2 = t;
        const ty = y1;
        y1 = y2;
        y2 = ty;
      }
      const length = GeometryUtils.pointsDistance(x1, y1, x2, y2);
      if (length < 1e-3) return;
      const angle = GeometryUtils.angleBetweenTwoPointsAndOrigin(x1, y1, x2, y2);
      const halfThick = line.getIn(['properties', 'thickness', 'length']) / 2 || 0;
      const offset = halfThick + 22;
      const label = formatWallLength(length, unit);
      parts.push(
        `<g transform="translate(${x1},${y1}) rotate(${angle},0,0)">` +
          `<g transform="translate(0,${offset})">` +
          `<text x="${length / 2}" y="-3" transform="scale(1,-1)" style="${TEXT_STYLE}">${escapeXml(
            label
          )}</text>` +
          `<line x1="0" y1="-5" x2="0" y2="5" style="${DIM_STYLE}"/>` +
          `<line x1="${length}" y1="-5" x2="${length}" y2="5" style="${DIM_STYLE}"/>` +
          `<line x1="0" y1="0" x2="${length}" y2="0" style="${DIM_STYLE}"/>` +
          `</g></g>`
      );
    });
  });

  return `<g id="export-wall-dimensions" pointer-events="none">${parts.join('')}</g>`;
}

function findMainFloorSvg() {
  const svgElements = document.getElementsByTagName('svg');
  if (!svgElements.length) return null;
  let maxEl = svgElements[0];
  for (let i = 1; i < svgElements.length; i++) {
    const w = svgElements[i].width && svgElements[i].width.baseVal && svgElements[i].width.baseVal.value;
    const mw = maxEl.width && maxEl.width.baseVal && maxEl.width.baseVal.value;
    if (w > mw) maxEl = svgElements[i];
  }
  return maxEl;
}

export function exportFloorplanToPdf(state, filename) {
  const source = findMainFloorSvg();
  if (!source) return Promise.reject(new Error('No floor plan SVG found'));

  const clone = source.cloneNode(true);
  const paper = clone.querySelector('#svg-drawing-paper');
  if (paper) paper.insertAdjacentHTML('beforeend', wallDimensionMarkup(state));

  const serializer = new XMLSerializer();
  const svgStr = serializer.serializeToString(clone);
  const svgDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgStr);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const w = source.width.baseVal.value;
      const h = source.height.baseVal.value;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      const png = canvas.toDataURL('image/png');

      const landscape = w >= h;
      const pdf = new jsPDF({
        orientation: landscape ? 'landscape' : 'portrait',
        unit: 'pt',
        format: 'a4'
      });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 24;
      const maxW = pageW - margin * 2;
      const maxH = pageH - margin * 2;
      const scale = Math.min(maxW / w, maxH / h);
      const dw = w * scale;
      const dh = h * scale;
      const ox = (pageW - dw) / 2;
      const oy = (pageH - dh) / 2;
      pdf.addImage(png, 'PNG', ox, oy, dw, dh);
      pdf.save(filename);
      resolve();
    };
    img.onerror = () => reject(new Error('Failed to render plan for PDF'));
    img.src = svgDataUrl;
  });
}
